import { FormField } from '@app/features/form/Components/FormFields';
import { FieldConfig, FieldGroup } from '@app/types/form.types';
import { useFormNavigationGuard } from '@hooks/useFormNavigationGuard';
import { Button, Select } from 'antd';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { DefaultValues, FieldErrors, FieldValues, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export const SKIP_FORM_RESET = Symbol('SKIP_FORM_RESET');

function getColSpan(width: string = 'full') {
  switch (width) {
    case 'sixth':
      return 'col-span-12 sm:col-span-2';
    case 'quarter':
      return 'col-span-12 sm:col-span-3';
    case 'third':
      return 'col-span-12 sm:col-span-4';
    case 'half':
      return 'col-span-12 sm:col-span-6';
    case 'twoThirds':
      return 'col-span-12 sm:col-span-8';
    case 'full':
    default:
      return 'col-span-12';
  }
}

// The count is its own element beside the translated title, so translators never
// have to carry the parentheses. Nothing renders while the count is unknown.
function TabLabel({ title, count }: { title: string; count?: number | null }) {
  return (
    <>
      {title}
      {typeof count === 'number' && <span className="ml-1.5 text-xs font-normal opacity-70">({count})</span>}
    </>
  );
}

type FormProps<T extends FieldValues> = {
  fields: FieldGroup[];
  onSubmit?:
    | SubmitHandler<T>
    | ((data: T) => Promise<T | void | typeof SKIP_FORM_RESET> | T | void | typeof SKIP_FORM_RESET);
  defaultValues?: DefaultValues<T>;
  extraClasses?: string;
  hideSubmit?: boolean;
  /**
   * Label for the submit button. Defaults to Save/Create chosen by whether the
   * route carries an `:id` — which reads the URL as a proxy for "editing an
   * existing record", and is wrong for any form that edits something the route
   * does not name (the signed-in user's own account, say, where it said
   * "Create").
   */
  submitLabel?: string;
  onFormChange?: (data: T, methods: ReturnType<typeof useForm<T>>) => void;
  enableNavigationGuard?: boolean;
  activeTab?: number;
  onActiveTabChange?: (index: number) => void;
  /**
   * Rendered directly under the Save button, on desktop and mobile alike, for a shortcut
   * that belongs beside saving without being part of the form. Independent of
   * `hideSubmit`, so a tab that hides Save does not take it down with it.
   */
  submitFooter?: ReactNode;
};

export function FormWrapper<T extends FieldValues>({
  fields,
  onSubmit,
  defaultValues,
  extraClasses = 'min-h-screen',
  hideSubmit = false,
  submitLabel,
  onFormChange,
  enableNavigationGuard = true,
  activeTab: activeTabProp,
  onActiveTabChange,
  submitFooter,
}: FormProps<T>) {
  const { t } = useTranslation();
  const methods = useForm<T>({
    defaultValues,
    mode: 'onSubmit',
  });

  const { id } = useParams();
  // Track the active section by its index, not by its (translated) title. Titles come from
  // `t(...)` and change identity when the language switches, which would leave `activeTab`
  // pointing at a title that no longer matches any group — hiding every section and making
  // it look like the form data was lost.
  const [ownActiveTab, setOwnActiveTab] = useState(0);
  // A parent that remounts this form (the visit form does, to re-seed its uncontrolled
  // services table after a save) can hold the index itself so the remount doesn't throw the
  // user back to the first section.
  const selectedTab = activeTabProp ?? ownActiveTab;
  // Sections come and go with the data, so a held index can outlive its section.
  const activeTab = selectedTab < fields.length ? selectedTab : 0;
  const setActiveTab = useCallback(
    (index: number) => {
      setOwnActiveTab(index);
      onActiveTabChange?.(index);
    },
    [onActiveTabChange]
  );
  const errors = methods.formState.errors as FieldErrors<T>;
  const isDirty = methods.formState.isDirty;
  const isSubmitting = methods.formState.isSubmitting;

  // Enable navigation guard to prevent data loss
  useFormNavigationGuard({
    isDirty,
    enabled: enableNavigationGuard,
  });

  // Re-seed the form baseline so it is only considered dirty once the user actually
  // edits something. react-hook-form derives `isDirty` by comparing the current values
  // against the baseline (defaultValues), so the baseline has to match the values the
  // form actually holds after it has finished setting up:
  //
  //  - Create forms mount with `defaultValues === undefined`; some field components
  //    normalise/write their value on mount, which left the form dirty before any
  //    interaction. Re-seeding from the freshly-mounted values keeps it clean.
  //  - Edit forms hydrate asynchronously after a fetch; the previous code re-seeded with
  //    `{ keepDefaultValues: true }`, which kept the original (empty) baseline and so the
  //    hydrated values always looked "changed". Letting the hydrated values become the new
  //    baseline clears that false dirty state.
  //
  // Keyed on the serialised content (not object identity) so parents that rebuild the
  // defaultValues object on every render don't wipe the user's in-progress edits.
  const defaultValuesKey = JSON.stringify(defaultValues ?? null);
  useEffect(() => {
    methods.reset((defaultValues ?? methods.getValues()) as T);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValuesKey]);

  // Watch all form values and call onFormChange when they change
  const formValues = methods.watch();
  useEffect(() => {
    if (onFormChange) {
      onFormChange(formValues, methods);
    }
  }, [formValues, onFormChange, methods]);

  // Check if any field in the group has an error
  const hasGroupError = (group: FieldGroup) =>
    group.fields.some((field: FieldConfig<unknown>) => {
      const keys = field.name.split('.');
      let err: unknown = errors;
      for (const key of keys) {
        if (!err || typeof err !== 'object' || !(key in (err as object))) return false;
        err = (err as Record<string, unknown>)[key];
      }
      return !!err && typeof err === 'object' && 'type' in (err as object);
    });

  const renderAllGroups = () => (
    <>
      {fields.map((group, i) => {
        const isActive = activeTab === i;
        return (
          <div key={group.title || i} className={`${isActive ? 'block' : 'hidden'} grid grid-cols-12 gap-4 sm:gap-6`}>
            {group.fields
              .filter((field) => !field.hidden)
              .map((field) => (
                <div key={field.name} className={getColSpan(field.width)}>
                  <FormField field={field} />
                </div>
              ))}
          </div>
        );
      })}
    </>
  );

  const renderVerticalTabs = () => {
    const activeGroup = fields[activeTab];
    return (
      <div className="hidden md:flex flex-col w-48 border-r border-gray-200">
        {fields.map((group, i) => {
          const isActive = activeTab === i;
          const hasError = hasGroupError(group);
          return (
            <button
              type="button"
              key={group.title || i}
              onClick={() => setActiveTab(i)}
              className={`text-left cursor-pointer px-4 py-3 font-medium border-l-4 transition-colors
                ${
                  isActive
                    ? `border-[var(--color-sky-600)] ${hasError ? 'text-red-500' : 'text-[var(--color-sky-600)]'} bg-gray-50`
                    : hasError
                      ? 'border-red-500 text-red-500 hover:bg-gray-50'
                      : 'border-transparent text-gray-600 hover:text-[var(--color-sky-600)] hover:border-[var(--color-sky-600)] hover:bg-gray-50'
                }
              `}
            >
              <TabLabel title={group.title || t('form-section-fallback', { number: i + 1 })} count={group.count} />
            </button>
          );
        })}

        {/* Desktop Submit button */}
        {!hideSubmit && onSubmit && activeGroup && !activeGroup?.hideSubmit && (
          <div className="mt-4 px-4 w-full">
            <Button type="primary" htmlType="submit" size="large" block loading={isSubmitting}>
              {submitLabel ?? (id ? t('common-save') : t('common-create'))}
            </Button>
          </div>
        )}

        {submitFooter && <div className="mt-2 px-4 w-full">{submitFooter}</div>}
      </div>
    );
  };

  const renderMobileTabs = () => (
    <div className="md:hidden mb-4">
      <Select
        value={activeTab}
        onChange={(value) => setActiveTab(value)}
        className="w-full"
        options={fields.map((group, i) => ({
          label: <TabLabel title={group.title || t('form-section-fallback', { number: i + 1 })} count={group.count} />,
          value: i,
        }))}
      />
    </div>
  );

  const submitForm = methods.handleSubmit(
    async (data) => {
      if (onSubmit) {
        const result = await onSubmit(data);
        if (result === SKIP_FORM_RESET) return;
        // Reset dirty state with the result from onSubmit (which should be the server response)
        // If onSubmit doesn't return anything, use the submitted data
        const resetValues = (result ?? data) as T;
        methods.reset(resetValues, { keepValues: true });
      }
    },
    () => {
      const firstErrorIndex = fields.findIndex((g) => hasGroupError(g));
      if (firstErrorIndex >= 0) setActiveTab(firstErrorIndex);
      const firstErrorField = Object.keys(errors)[0];
      if (firstErrorField) {
        const el = document.querySelector(`[name="${firstErrorField}"]`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  );

  // react-hook-form happily runs a second submission on top of the first, so a double-click
  // sent the request twice. The guard is a ref because both clicks can land before React has
  // re-rendered with `isSubmitting`. It also covers the modal footer, which submits by
  // dispatching a native `submit` event on the form rather than by pressing a button here.
  const isSubmittingRef = useRef(false);
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    if (isSubmittingRef.current) {
      event.preventDefault();
      return;
    }
    isSubmittingRef.current = true;
    void submitForm(event).finally(() => {
      isSubmittingRef.current = false;
    });
  };

  // Prevent Enter key from submitting the form
  const handleKeyDown = (e: React.KeyboardEvent<HTMLFormElement>) => {
    if (e.key === 'Enter' && e.target instanceof HTMLInputElement) {
      // Allow Enter in textarea elements
      if ((e.target as HTMLInputElement).type !== 'textarea') {
        e.preventDefault();
      }
    }
  };

  const activeGroup = fields[activeTab];

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={onSubmit ? handleSubmit : undefined}
        onKeyDown={handleKeyDown}
        className={`bg-white py-2 ${extraClasses} relative flex flex-col md:flex-row`}
      >
        {/* Desktop sidebar */}
        {renderVerticalTabs()}

        {/* Main content including mobile dropdown + content + mobile button */}
        <div className="flex flex-col w-full md:flex-1 md:pl-6">
          {/* Mobile dropdown */}
          {renderMobileTabs()}

          {/* Form content */}
          {renderAllGroups()}

          {/* Mobile Submit button */}
          {!hideSubmit && onSubmit && activeGroup && !activeGroup?.hideSubmit && (
            <div className="md:hidden mt-6 px-4 w-full">
              <Button type="primary" htmlType="submit" size="large" block loading={isSubmitting}>
                {submitLabel ?? (id ? t('common-save') : t('common-create'))}
              </Button>
            </div>
          )}

          {submitFooter && <div className="md:hidden mt-2 px-4 w-full">{submitFooter}</div>}
        </div>
      </form>
    </FormProvider>
  );
}
